import React, { useState, useEffect } from "react";
import {
  Container,
  Paper,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  TablePagination,
} from "@mui/material";
import { useAuth } from "../../contexts/AuthContext";
import { supabase } from "../../services/supabase";
import { Order } from "../../types";
import { useTranslation } from 'react-i18next';

// Extracted Components
import OrderManagementHeader from "./order-management/components/OrderManagementHeader";
import OrderFilters from "./order-management/components/OrderFilters";
import OrderRow from "./order-management/components/OrderRow";
import OrderStatusDialog from "./order-management/components/OrderStatusDialog";

// Date formatting helper
const formatDate = (dateString: string, format: string) => {
  const date = new Date(dateString);
  if (format === "MMM dd, yyyy") {
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    return `${months[date.getMonth()]} ${date
      .getDate()
      .toString()
      .padStart(2, "0")}, ${date.getFullYear()}`;
  } else if (format === "HH:mm") {
    return `${date.getHours().toString().padStart(2, "0")}:${date
      .getMinutes()
      .toString()
      .padStart(2, "0")}`;
  }
  return dateString;
};

const OrderManagement: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [expandedOrders, setExpandedOrders] = useState<Set<string>>(new Set());
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    fetchOrders();
  }, [user]);

  const fetchOrders = async () => {
    setLoading(true);
    setError("");
    try {
      const { data, error } = await supabase
        .from("orders")
        .select(
          `
          *,
          order_items (
            *,
            product:products (*)
          )
        `
        )
        .eq("seller_id", user?.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setOrders(data || []);
    } catch (err: any) {
      setError(t('common.error'));
      console.error("Error fetching orders:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleExpandClick = (orderId: string) => {
    const newExpanded = new Set(expandedOrders);
    if (newExpanded.has(orderId)) {
      newExpanded.delete(orderId);
    } else {
      newExpanded.add(orderId);
    }
    setExpandedOrders(newExpanded);
  };

  const handleStatusUpdate = async (newStatus: string) => {
    if (!selectedOrder) return;

    try {
      const { error } = await supabase
        .from("orders")
        .update({ status: newStatus })
        .eq("id", selectedOrder.id);

      if (error) throw error;

      setOrders(
        orders.map((order) =>
          order.id === selectedOrder.id
            ? { ...order, status: newStatus as Order["status"] }
            : order
        )
      );
      setSuccess(t('common.success'));
      setTimeout(() => setSuccess(""), 3000);
    } catch (err: any) {
      setError(t('common.error'));
    }

    setStatusDialogOpen(false);
    setSelectedOrder(null);
  };

  const filteredOrders = orders.filter((order) => {
    const matchesSearch =
      order.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.shipping_address.full_name
        ?.toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
      order.shipping_address.phone
        ?.toLowerCase()
        .includes(searchQuery.toLowerCase());
    const matchesStatus = !filterStatus || order.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const paginatedOrders = filteredOrders.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 6 }}>
      <OrderManagementHeader />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      <OrderFilters
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        filterStatus={filterStatus}
        onStatusChange={setFilterStatus}
      />

      <Paper
        sx={{
          bgcolor: "background.paper",
          borderRadius: "8px",
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
        }}
      >
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell width={50} />
                    <TableCell sx={{ minWidth: 100 }}>{t('admin.orderId')}</TableCell>
                    <TableCell sx={{ display: { xs: "none", sm: "table-cell" }, minWidth: 100 }}>
                      {t('admin.date')}
                    </TableCell>
                    <TableCell sx={{ minWidth: 150 }}>{t('admin.customer')}</TableCell>
                    <TableCell sx={{ display: { xs: "none", md: "table-cell" }, minWidth: 80 }}>
                      {t('admin.items')}
                    </TableCell>
                    <TableCell align="right" sx={{ minWidth: 100 }}>
                      {t('admin.total')}
                    </TableCell>
                    <TableCell sx={{ display: { xs: "none", lg: "table-cell" }, minWidth: 100 }}>
                      {t('admin.payment')}
                    </TableCell>
                    <TableCell sx={{ minWidth: 100 }}>{t('common.status')}</TableCell>
                    <TableCell align="center" sx={{ minWidth: 80 }}>
                      {t('common.actions')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedOrders.map((order) => (
                    <OrderRow
                      key={order.id}
                      order={order}
                      isExpanded={expandedOrders.has(order.id)}
                      onExpandClick={handleExpandClick}
                      onUpdateStatusClick={(order) => {
                        setSelectedOrder(order);
                        setStatusDialogOpen(true);
                      }}
                      formatDate={formatDate}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredOrders.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                borderTop: "1px solid",
                borderTopColor: "divider",
                ".MuiTablePagination-toolbar": {
                  flexWrap: { xs: "wrap", sm: "nowrap" },
                  justifyContent: { xs: "center", sm: "flex-end" },
                  gap: { xs: 1, sm: 0 },
                },
                ".MuiTablePagination-selectLabel": {
                  display: { xs: "none", sm: "block" },
                },
              }}
            />
          </>
        )}
      </Paper>

      <OrderStatusDialog
        open={statusDialogOpen}
        onClose={() => setStatusDialogOpen(false)}
        onUpdate={handleStatusUpdate}
        selectedOrder={selectedOrder}
        onOrderChange={setSelectedOrder}
      />
    </Container>
  );
};

export default OrderManagement;
